import * as Yup from "yup";
import { useFormik } from "formik";
import DatePicker from "react-datepicker";
import MaskedInput from "react-text-mask";
import { toastr } from "react-redux-toastr";
import { useAuth0 } from "@auth0/auth0-react";
import Select, { components } from "react-select";
import React, { useEffect, useState } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { Dialog, Box, Grid } from "@material-ui/core";
import { DialogContent, Typography } from "@material-ui/core";
import CircularProgress from "@mui/material/CircularProgress";
// components
import MyButton from "components/MyButton";
import Loader from "components/loader/Loader";
import PastoralService from "Lib/API/PastoralApi";
import { InputField, LabelField } from "components";
import { getDateFormat, maskDate } from "helper/dateFormat";
import BottomArrowSvg from "assets/icons/svg/BottomArrowSvg";
import { getDateFormatForDatePicket } from "helper/dateFormat";
import createAutoCorrectedDatePipe from "text-mask-addons/dist/createAutoCorrectedDatePipe";
// direct import
import "./styles.scss";
import NewTaskSuccess from "./NewTaskSuccess";

// Select styles
const selectControlStyles = {
  backgroundColor: "#F3F6F9",
  borderColor: "#F3F6F9",
  color: "#3F4254",
  paddingTop: "6px",
  paddingBottom: "6px",
  cursor: "pointer",
};

const colourStyles = {
  control: (styles) => ({ ...styles, ...selectControlStyles }),
  menuList: (styles) => ({ ...styles, maxHeight: "150px" }),
};

const DropdownIndicator = (props) => (
  <components.DropdownIndicator {...props}>
    <BottomArrowSvg label="Arrow" />
  </components.DropdownIndicator>
);

const formatOptionLabel = ({ label, _id }) => (
  <div className="dropdown-container" key={_id}>
    <div className="icon-name-container d-flex align-items-center justify-content-start">
      <div className="name-container" style={{ marginInlineStart: 10 }}>
        <div>{label}</div>
      </div>
    </div>
  </div>
);

const NewTask = ({ closeNewTaskModal, taskOpen, taskCloseModal }) => {
  const dateMask = maskDate();
  const placeholderFormat = getDateFormat();
  const { getAccessTokenSilently } = useAuth0();
  const dateFormat = getDateFormatForDatePicket();
  const autoCorrectedDatePipe = createAutoCorrectedDatePipe(dateFormat);
  // state variables
  const [userData, setUserData] = useState([]);
  const [responseData, setResponseData] = useState([]);
  const [getMembersLoading, setGetMembersLoading] = useState(false);
  const [successFullTask, setSuccessFullTask] = useState(false);
  const [createTaskLoading, setCreateTaskLoading] = useState(false);
  const [getEventUsersLoading, setGetEventUsersLoading] = useState(false);

  useEffect(() => {
    fetchUsersApi();
    fetchMembersApi();
  }, []);

  const fetchUsersApi = async () => {
    setGetEventUsersLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const res = await PastoralService.getEventUsers({ token, page: 1 });
      setUserData(res?.users || []);
    } catch (err) {
      toastr.error("Error", err?.data?.message || "Something went wrong");
    } finally {
      setGetEventUsersLoading(false);
    }
  };

  const fetchMembersApi = async () => {
    setGetMembersLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const res = await PastoralService.getMembers({ token, page: 1 });
      setResponseData(res?.contacts || []);
    } catch (err) {
      toastr.error("Error", err?.data?.message || "Something went wrong");
    } finally {
      setGetMembersLoading(false);
    }
  };

  const options1 = userData.map((item) => ({
    label: `${item?.type} ${item?.fullName ? "- " + item?.fullName : ""}`,
    value: item?.id,
  }));

  const options2 = responseData.map((item) => ({
    label: item?.fullName,
    value: item?.id,
  }));

  const actionTypeOptions = [
    { value: "1", label: "SMS" },
    { value: "2", label: "EMAIL" },
    { value: "3", label: "CALL" },
    { value: "4", label: "Home VISIT" },
    { value: "5", label: "Hospital VISIT" },
    { value: "10", label: "Other" },
  ];

  const convertUTCToLocalDate = (date) => {
    if (!date) return date;
    const d = new Date(date);
    return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  };

  const convertLocalToUTCDate = (date) => {
    if (!date) return date;
    const d = new Date(date);
    return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  };

  const formik = useFormik({
    initialValues: {
      assignTo: { id: "", name: "" },
      contactWith: { id: "", name: "" },
      actionType: { id: "", name: "" },
      taskDescription: "",
      taskDate: "",
    },
    validationSchema: Yup.object({
      assignTo: Yup.object().shape({
        id: Yup.string().required("Assign To is required"),
      }),
      actionType: Yup.object().shape({
        id: Yup.string().required("Action Type is required"),
      }),
      taskDescription: Yup.string().required("Task Description is required"),
      taskDate: Yup.date().required("Task Date is required"),
    }),
    onSubmit: async (values) => {
      setCreateTaskLoading(true);
      try {
        const token = await getAccessTokenSilently();
        const input = {
          date: values.taskDate,
          clergyType: values.assignTo.id.toString(),
          description: values.taskDescription,
          actionTypeId: values.actionType.id.toString(),
          ContactWithId: values.contactWith.id
            ? values.contactWith.id.toString()
            : undefined,
        };
        await PastoralService.createTask({ data: input, token });
        setSuccessFullTask(true);
      } catch (err) {
        toastr.error("Error", err?.data?.message || "Something went wrong");
      } finally {
        setCreateTaskLoading(false);
      }
    },
  });

  const { values, errors, touched, handleSubmit, setFieldValue } = formik;

  return (
    <Dialog
      open={taskOpen}
      onClose={() =>
        !successFullTask ? closeNewTaskModal() : taskCloseModal()
      }
      maxWidth="md"
      fullWidth
    >
      <DialogContent>
        {!successFullTask ? (
          <>
            <Typography
              variant="h5"
              align="center"
              className="NewEvent-heading"
            >
              New Task
            </Typography>

            <Box mt={4}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <InputField
                    {...{
                      ...formik,
                      mandatory: true,
                      title: "Task Description",
                      name: "taskDescription",
                      placeholder: "Please Add Description",
                      value: formik.values?.taskDescription,
                    }}
                  />
                  {touched.taskDescription && errors.taskDescription && (
                    <Typography color="error">
                      {errors.taskDescription}
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <LabelField title="Task Date" mandatory />
                  <DatePicker
                    isClearable={!!values.taskDate}
                    showIcon
                    dateFormat={dateFormat}
                    placeholderText={placeholderFormat}
                    selected={
                      values.taskDate && convertUTCToLocalDate(values.taskDate)
                    }
                    onChange={(date) =>
                      setFieldValue("taskDate", convertLocalToUTCDate(date))
                    }
                    minDate={new Date()}
                    customInput={
                      <MaskedInput
                        pipe={autoCorrectedDatePipe}
                        mask={dateMask}
                        keepCharPositions
                        guide
                      />
                    }
                  />
                  {touched.taskDate && errors.taskDate && (
                    <Typography color="error">{errors.taskDate}</Typography>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <LabelField title="Assign To" mandatory />
                  <Select
                    classNamePrefix="react-select"
                    options={options1}
                    isLoading={getEventUsersLoading}
                    styles={colourStyles}
                    value={
                      values.assignTo.id
                        ? {
                            value: values.assignTo.id,
                            label: values.assignTo.name,
                          }
                        : ""
                    }
                    onChange={(val) =>
                      setFieldValue("assignTo", {
                        id: val.value,
                        name: val.label,
                      })
                    }
                    components={{ DropdownIndicator }}
                    formatOptionLabel={formatOptionLabel}
                  />
                  {touched.assignTo?.id && errors.assignTo?.id && (
                    <Typography color="error">{errors.assignTo.id}</Typography>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <LabelField title="Contact With" />
                  <Select
                    classNamePrefix="react-select"
                    options={options2}
                    isLoading={getMembersLoading}
                    styles={colourStyles}
                    value={
                      values.contactWith.id
                        ? {
                            value: values.contactWith.id,
                            label: values.contactWith.name,
                          }
                        : ""
                    }
                    onChange={(val) =>
                      setFieldValue("contactWith", {
                        id: val.value,
                        name: val.label,
                      })
                    }
                    components={{ DropdownIndicator }}
                    formatOptionLabel={formatOptionLabel}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <LabelField title="Action Type" mandatory />
                  <Select
                    classNamePrefix="react-select"
                    options={actionTypeOptions}
                    styles={colourStyles}
                    value={
                      values.actionType.id
                        ? {
                            value: values.actionType.id,
                            label: values.actionType.name,
                          }
                        : ""
                    }
                    onChange={(val) =>
                      setFieldValue("actionType", {
                        id: val.value,
                        name: val.label,
                      })
                    }
                    components={{ DropdownIndicator }}
                    formatOptionLabel={formatOptionLabel}
                  />
                  {touched.actionType?.id && errors.actionType?.id && (
                    <Typography color="error">
                      {errors.actionType.id}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Box>

            <Box
              display="flex"
              justifyContent="space-between"
              sx={{ mt: 15, mb: 3 }}
            >
              <MyButton
                type="secondary"
                content="Close"
                onClick={closeNewTaskModal}
              />
              <MyButton
                type="primary"
                content={
                  createTaskLoading ? (
                    <CircularProgress size={20} style={{ color: "#fff" }} />
                  ) : (
                    "Save"
                  )
                }
                onClick={handleSubmit}
                disabled={createTaskLoading}
                variant="outlined"
                style={{ minWidth: 103 }}
              />
            </Box>
          </>
        ) : createTaskLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="100%"
            height="100%"
          >
            <Loader />
          </Box>
        ) : (
          <NewTaskSuccess taskCloseModal={taskCloseModal} />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NewTask;
